import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchGoogleReviews, getStarProperties } from "../utils/googleReviews";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
interface GoogleReview {
  author_name: string;
  rating: number;
  text: string;
  profile_photo_url?: string;
  time: number;
}
const GoogleReviews = () => {
  const [selectedReview, setSelectedReview] = useState<GoogleReview | null>(null);
  
  const { data, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['google-reviews'],
    queryFn: async () => {
      const response = await fetchGoogleReviews();
      if (response.errorType) {
        throw { type: response.errorType, message: response.errorMessage || "Erreur lors de la récupération des avis" };
      }
      return response.reviews as GoogleReview[];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  const reviews = data || [];
  const error = queryError ? { type: (queryError as any).type || "UNKNOWN", message: (queryError as any).message || "Erreur inattendue" } : null;

  // Fonction pour obtenir un message d'erreur adapté selon le type d'erreur
  const getErrorToastMessage = (errorType: string): string => {
    switch (errorType) {
      case "INVALID_REQUEST":
        return "La demande d'avis Google est invalide. Veuillez contacter l'administrateur.";
      case "REQUEST_DENIED":
        return "L'accès à l'API Google a été refusé. Vérifiez votre clé API.";
      case "OVER_QUERY_LIMIT":
        return "Le quota d'appels à l'API Google a été dépassé. Veuillez réessayer plus tard.";
      case "NOT_FOUND":
        return "Établissement non trouvé sur Google. Vérifiez l'identifiant de lieu.";
      case "API_ERROR":
        return "Erreur lors de la communication avec l'API Google. Veuillez réessayer.";
      case "NO_REVIEWS":
        return "Aucun avis Google n'a été trouvé pour cet établissement.";
      default:
        return "Impossible de charger les avis Google. Veuillez réessayer plus tard.";
    }
  };

  // This function can safely use JSX in a .tsx file
  const renderStars = (rating: number) => {
    return getStarProperties(rating).map((star, i) => <svg key={i} className={`w-5 h-5 ${star.isFilled ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
      </svg>);
  };
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const isTextTruncated = (text: string) => {
    return text.length > 200;
  };

  const formatAuthorName = (fullName: string): string => {
    const parts = fullName.trim().split(/\s+/);
    
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    }
    
    const firstName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    const lastName = parts[parts.length - 1];
    
    // Si le nom de famille est un nombre, l'afficher en entier
    if (/^\d+$/.test(lastName)) {
      return `${firstName} ${lastName}`;
    }
    
    // Sinon, afficher uniquement l'initiale
    const lastNameInitial = lastName.charAt(0).toUpperCase();
    return `${firstName} ${lastNameInitial}.`;
  };
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10 max-w-6xl mx-auto">
        {[...Array(3)].map((_, i) => <div key={i} className="card bg-white animate-pulse">
            <div className="flex items-center mb-4">
              <div className="h-6 w-24 bg-gray-200 rounded"></div>
            </div>
            <div className="h-20 bg-gray-200 rounded mb-4"></div>
            <div className="h-6 w-16 bg-gray-200 rounded"></div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-10">
        <Alert variant="destructive" className="mb-6 max-w-2xl mx-auto">
          <AlertTitle>Erreur lors du chargement des avis</AlertTitle>
          <AlertDescription>
            {error.message}
            {error.type === "REQUEST_DENIED" && <p className="mt-2 text-sm">
                Vérifiez les restrictions de votre clé API Google Places et assurez-vous qu'elle autorise les appels depuis l'URL Supabase.
              </p>}
          </AlertDescription>
        </Alert>
        <p className="text-center mb-4">Nos clients nous adorent! N'hésitez pas à consulter nos avis sur Google.</p>
        <a href="https://g.page/bio-beaute-zen/review" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center">
          Voir nos avis sur Google
        </a>
      </div>;
  }
  if (reviews.length === 0) {
    return <div className="text-center py-10">
        <p className="mb-4">Aucun avis trouvé pour le moment.</p>
        <a href="https://g.page/bio-beaute-zen/review" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center">
          Laisser un avis sur Google
        </a>
      </div>;
  }
  const firstRowReviews = reviews.slice(0, 3);
  const secondRowReviews = reviews.slice(3, 6);

  return <div className="mt-10 max-w-6xl mx-auto">
      {/* Première ligne - 3 avis */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {firstRowReviews.map((review, index) => <div key={index} className="card bg-white hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center mb-4 gap-2">
              {review.profile_photo_url && <img src={review.profile_photo_url} alt={review.author_name} className="w-8 h-8 rounded-full" />}
              <div>
                <p className="font-medium">{formatAuthorName(review.author_name)}</p>
                <div className="flex">
                  {renderStars(review.rating)}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <p className="line-clamp-4 text-sm italic">
                "{review.text}"
              </p>
              {isTextTruncated(review.text) && (
                <button
                  onClick={() => setSelectedReview(review)}
                  className="text-[hsl(110,25%,35%)] hover:text-[hsl(110,25%,30%)] font-medium text-xs transition-colors mt-1 inline-block"
                >
                  Voir plus...
                </button>
              )}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">{formatDate(review.time)}</p>
              <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" alt="Logo Google" loading="lazy" className="h-4 md:h-5 w-auto" />
            </div>
          </div>)}
      </div>
      
      {/* Deuxième ligne - 2 avis centrés */}
      {secondRowReviews.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
          {secondRowReviews.map((review, index) => <div key={index + 3} className="card bg-white hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-4 gap-2">
                {review.profile_photo_url && <img src={review.profile_photo_url} alt={review.author_name} className="w-8 h-8 rounded-full" />}
                <div>
                  <p className="font-medium">{formatAuthorName(review.author_name)}</p>
                  <div className="flex">
                    {renderStars(review.rating)}
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <p className="line-clamp-4 text-sm italic">
                  "{review.text}"
                </p>
                {isTextTruncated(review.text) && (
                  <button
                    onClick={() => setSelectedReview(review)}
                    className="text-[hsl(110,25%,35%)] hover:text-[hsl(110,25%,30%)] font-medium text-xs transition-colors mt-1 inline-block"
                  >
                    Voir plus...
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{formatDate(review.time)}</p>
                <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" alt="Logo Google" loading="lazy" className="h-4 md:h-5 w-auto" />
              </div>
            </div>)}
        </div>
      )}

      {/* Dialog pour afficher l'avis complet */}
      <Dialog open={selectedReview !== null} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedReview?.profile_photo_url && (
                <img 
                  src={selectedReview.profile_photo_url} 
                  alt={selectedReview ? formatAuthorName(selectedReview.author_name) : ''} 
                  className="w-10 h-10 rounded-full"
                />
              )}
              <div className="flex-1">
                <div className="font-semibold">{selectedReview && formatAuthorName(selectedReview.author_name)}</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex">
                    {selectedReview && renderStars(selectedReview.rating)}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {selectedReview && formatDate(selectedReview.time)}
                  </span>
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm leading-relaxed italic">
              "{selectedReview?.text}"
            </p>
          </div>
          <div className="mt-6 flex justify-end">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" 
              alt="Logo Google" 
              loading="lazy" 
              className="h-5 w-auto"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>;
};
export default GoogleReviews;